import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, UserPlus, Crown, MessageCircleHeart, Sparkles, ShieldCheck, IndianRupee } from "lucide-react";
import ProfileCard from "@/components/ProfileCard";
import { publicApi } from "@/lib/account";
import PlansGrid from "@/components/PlansGrid";
import useSeo from "@/lib/seo";
import { usePrefs } from "@/lib/prefs";
import Spotlight from "@/components/Spotlight";
import { flagUrl, slugify } from "@/lib/countries";

const GlobalStrip = () => {
  const [list, setList] = useState([]);
  useEffect(() => { publicApi.get("/meta").then((r) => setList(r.data.countries)).catch(() => {}); }, []);
  const row = [...list, ...list];
  return (
    <section className="py-20 border-y border-amber-500/10 overflow-hidden" data-testid="global-strip">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold">A truly global community</p>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold mt-3">{list.length || 26} countries. Every faith. Every ethnicity.</h2>
          <p className="text-slate-400 mt-3 max-w-2xl">Inter-racial, inter-faith and cross-country marriages are celebrated here. Explore a country or find someone across the world.</p>
        </div>
        <Link to="/countries" data-testid="global-strip-all" className="text-amber-300 flex items-center gap-2">Explore all countries <ArrowRight className="w-4 h-4" /></Link>
      </div>
      <div className="mt-10 flex gap-4 w-max marquee">
        {row.map((c, i) => (
          <Link key={`${c.name}-${i}`} to={`/country/${slugify(c.name)}`} className="card-royal rounded-full pl-2 pr-5 py-2 flex items-center gap-3 shrink-0">
            {flagUrl(c.name) && <img src={flagUrl(c.name)} alt="" className="w-9 h-6 rounded object-cover" loading="lazy" />}
            <span className="text-sm whitespace-nowrap">{c.name}</span>
          </Link>
        ))}
      </div>
    </section>
  );
};

const HERO = "https://images.unsplash.com/photo-1722952934708-749c22eb2e58?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const CEREMONY = "https://images.unsplash.com/photo-1599462616558-2b75fd26a283?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const STEPS = [
  { icon: UserPlus, title: "Create your profile", text: "Tell your story, add your photographs and what matters to your family." },
  { icon: Crown, title: "Try from ₹9", text: "Start with a 1-day or 7-day trial, or save with Silver, Gold and Platinum." },
  { icon: MessageCircleHeart, title: "Start talking", text: "Message anyone who interests you — ₹1 per message at home, ₹30 across borders." },
];

const Hero = ({ stats, t }) => (
  <section className="relative min-h-[92vh] flex items-end overflow-hidden grain" data-testid="hero-section">
    <motion.img initial={{ scale: 1.15 }} animate={{ scale: 1 }} transition={{ duration: 2.4, ease: "easeOut" }}
      src={HERO} alt="Wedding couple" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/55 to-black/10" />
    <div className="absolute inset-0 bg-gradient-to-t from-[#08090C] via-transparent to-transparent" />
    <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pb-20 pt-32 w-full">
      <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
        className="text-xs uppercase tracking-[0.3em] text-amber-400 font-semibold">{t("kicker")}</motion.p>
      <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.8 }}
        className="font-display text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[1.02] tracking-tight mt-5 max-w-3xl" data-testid="hero-title">
        {t("t1")}<span className="gold-text italic">{t("t2")}</span>{t("t3")}
      </motion.h1>
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }}
        className="text-slate-300 text-base md:text-lg mt-6 max-w-xl leading-relaxed">
        {t("sub")}
      </motion.p>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }} className="flex flex-wrap gap-4 mt-10">
        <Link to="/browse" data-testid="hero-browse-button" className="btn-gold px-8 py-3.5 rounded-full font-medium flex items-center gap-2">
          {t("browseBtn")} <ArrowRight className="w-4 h-4" />
        </Link>
        <Link to="/me" data-testid="hero-create-profile-button" className="btn-ghost px-8 py-3.5 rounded-full font-medium backdrop-blur">{t("createBtn")}</Link>
      </motion.div>
      <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl" data-testid="hero-stats">
        {[[stats?.profiles ?? "200+", "Profiles"], [stats?.countries ?? 26, "Countries"], ["₹1 / ₹30", "Local / cross-border msg"], ["All", "Faiths & cultures"]].map(([v, l]) => (
          <div key={l} className="border-l border-amber-500/40 pl-4">
            <div className="font-display text-3xl text-amber-200">{v}</div>
            <div className="text-xs uppercase tracking-widest text-slate-400 mt-1">{l}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Steps = () => (
  <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24" data-testid="how-it-works-section">
    <p className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold">How it works</p>
    <h2 className="font-display text-4xl sm:text-5xl font-semibold mt-3 max-w-xl">Three steps to your forever.</h2>
    <div className="grid md:grid-cols-3 gap-6 mt-14">
      {STEPS.map((s, i) => (
        <motion.div key={s.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          transition={{ delay: i * 0.12 }} className={`card-royal rounded-2xl p-8 ${i === 1 ? "md:translate-y-10" : ""}`}>
          <div className="flex items-center justify-between">
            <s.icon className="w-8 h-8 text-amber-400" />
            <span className="font-display text-5xl text-amber-500/20">0{i + 1}</span>
          </div>
          <h3 className="font-display text-2xl mt-8">{s.title}</h3>
          <p className="text-sm text-slate-400 mt-3 leading-relaxed">{s.text}</p>
        </motion.div>
      ))}
    </div>
  </section>
);

const Featured = ({ items }) => (
  <section className="relative py-24 mandala-bg" data-testid="featured-section">
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold">Featured today</p>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold mt-3">Souls waiting to say hello.</h2>
        </div>
        <Link to="/browse" data-testid="featured-view-all" className="text-amber-300 flex items-center gap-2 hover:gap-3 transition-[gap]">View all profiles <ArrowRight className="w-4 h-4" /></Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
        {items.map((p, i) => <ProfileCard key={p.id} p={p} index={i} />)}
      </div>
    </div>
  </section>
);

const Story = () => (
  <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24 grid lg:grid-cols-12 gap-12 items-center" data-testid="story-section">
    <div className="lg:col-span-7 relative">
      <img src={CEREMONY} alt="Wedding ceremony" className="rounded-3xl w-full h-[480px] object-cover" />
      <Link to="/stories" data-testid="landing-stories-link" className="absolute -bottom-6 -right-2 md:right-8 card-royal rounded-2xl p-5 max-w-xs floaty bg-[#181B26]/90 backdrop-blur-xl">
        <p className="font-display text-xl italic text-amber-100">"Every great love story starts with a hello."</p>
        <p className="text-xs text-amber-300 mt-2">Read & share success stories →</p>
      </Link>
    </div>
    <div className="lg:col-span-5 space-y-6">
      <p className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold">Honest by design</p>
      <h2 className="font-display text-4xl font-semibold">Transparent, respectful, always clear.</h2>
      {[[Sparkles, "AI profiles are always labelled", "While our community grows, AI companions help you practise conversations — and they always say so."],
        [ShieldCheck, "Your photos stay yours", "Upload securely, remove any time."],
        [IndianRupee, "Pay only for what you send", "₹1 per message in your country, ₹30 across borders, deducted from your wallet. No hidden charges."]].map(([I, t, d]) => (
        <div key={t} className="flex gap-4">
          <I className="w-6 h-6 text-amber-400 shrink-0 mt-1" />
          <div><h3 className="text-lg font-medium">{t}</h3><p className="text-sm text-slate-400 mt-1">{d}</p></div>
        </div>
      ))}
    </div>
  </section>
);

export default function Landing() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const { t } = usePrefs();
  useSeo("Global Marriage Community – Brides & Grooms Worldwide", "Meet brides and grooms from 25+ countries. Inter-racial, inter-faith and international marriages welcome. Free profile, trial from ₹9.");
  useEffect(() => {
    publicApi.get("/profiles/featured").then((r) => setItems(r.data)).catch(() => {});
    publicApi.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  return (
    <div data-testid="landing-page">
      <Hero stats={stats} t={t} />
      <Steps />
      <GlobalStrip />
      <Featured items={items} />
      <Story />
      <Spotlight />
      <section className="py-24 border-t border-amber-500/10" data-testid="landing-plans-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <p className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold">Membership</p>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold mt-3 mb-12">{t("plansTitle")}</h2>
          <PlansGrid />
        </div>
      </section>
    </div>
  );
}
